import {
  CaretCircleRightIcon,
  Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Icon, IconKey } from '@sr/ui-library';
import { ComponentProps, ReactNode, useState } from 'react';
import { Menu } from 'react-daisyui';
import { NavLink } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import './AppSidebarMenu.css';

export interface IAppSidebarMenuSectionProps {
  children: ReactNode;
  className?: string;
  hasSeparator?: boolean;
  heading?: string;
}

export const AppSidebarMenuSection = ({
  children,
  className,
  hasSeparator = false,
  heading,
}: IAppSidebarMenuSectionProps) => {
  return (
    <div className={twMerge(className, 'app-submenu')}>
      <Menu
        className={`m-0 w-full gap-2 p-0 text-xl text-sr-black ${
          hasSeparator && 'border-t border-sr-outline'
        }`}
      >
        {heading && <AppSidebarMenuHeading label={heading} />}
        {children}
      </Menu>
    </div>
  );
};

export interface IAppSidebarMenuHeadingProps {
  label: string;
}

export const AppSidebarMenuHeading = ({
  label,
}: IAppSidebarMenuHeadingProps) => {
  return (
    <Menu.Title className="pb-2 pt-4 text-left text-xs text-read-only-text !opacity-50">
      <div className="font-medium uppercase">{label}</div>
    </Menu.Title>
  );
};

export interface IAppSidebarMenuItemProps {
  icon?: IconKey | PhosphorIcon;
  label: ReactNode;
  linkTo: string;
  rel?: string;
  target?: ComponentProps<'a'>['target'];
}

export const AppSidebarMenuItem = ({
  icon,
  label,
  linkTo,
  rel,
  target,
}: IAppSidebarMenuItemProps) => {
  const isNewTabLink = target === '_blank';
  const shouldUseNativeLink = linkTo.startsWith('http') || isNewTabLink;
  const linkRel = isNewTabLink ? (rel ?? 'noopener noreferrer') : rel;

  const linkElement = shouldUseNativeLink ? (
    <a
      className="flex flex-[1_0_0] gap-3 border border-transparent px-3 py-[6px] text-sm font-normal leading-6"
      href={linkTo}
      rel={linkRel}
      target={target}
      onClick={(e) => {
        // Remove focus from the link after clicking to prevent active state
        (e.target as HTMLAnchorElement).blur();
      }}
    >
      {label}
    </a>
  ) : (
    <NavLink
      className={({ isActive }) => {
        return twMerge(
          'flex flex-[1_0_0] gap-3 border border-transparent px-3 py-[6px] text-sm font-normal leading-6',
          isActive ? 'active' : '',
        );
      }}
      rel={linkRel}
      target={target}
      to={linkTo}
    >
      {icon && (
        <div className="flex h-6 w-6 flex-col items-center justify-center gap-3">
          <Icon className="size-5 flex-shrink-0" icon={icon} />
        </div>
      )}
      {label}
    </NavLink>
  );

  return (
    <Menu.Item className="flex h-[38px] w-full justify-between text-sr-white">
      {linkElement}
    </Menu.Item>
  );
};

export interface IAppSidebarMenuExpandingGroupProps {
  children: ReactNode;
  heading: string;
  icon?: IconKey | PhosphorIcon;
}

export const AppSidebarMenuExpandingGroup = ({
  children,
  heading,
  icon,
}: IAppSidebarMenuExpandingGroupProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const expandIcon = (
    <div className="flex h-6 w-[36.16px] flex-col items-center justify-center gap-3">
      <Icon
        className="arrow-animation !h-[18px] !w-[18px] flex-shrink-0"
        icon={CaretCircleRightIcon}
        size="sm"
      />
    </div>
  );

  return (
    <Menu.Item className="flex w-full justify-between text-sr-white">
      <Menu.Dropdown
        label={
          <>
            {icon && (
              <div className="flex h-6 w-[36.16px] flex-col items-center justify-center gap-3">
                <Icon className="size-5 flex-shrink-0" icon={icon} />
              </div>
            )}
            <div className="w-full whitespace-nowrap text-sm font-medium">
              {heading}
            </div>
            {expandIcon}
          </>
        }
        className="flex w-60 gap-3 border border-transparent px-3 py-[6px]"
        open={
          location.pathname.includes(
            heading.toLowerCase().replace(/ /g, '-'),
          ) || isOpen
        }
        onClick={() =>
          location.pathname.includes(heading.toLowerCase().replace(/ /g, '-'))
            ? setIsOpen(true)
            : setIsOpen(!isOpen)
        }
      >
        {children}
      </Menu.Dropdown>
    </Menu.Item>
  );
};

const AppSidebarMenu = {
  ExpandingGroup: AppSidebarMenuExpandingGroup,
  Heading: AppSidebarMenuHeading,
  Item: AppSidebarMenuItem,
  Section: AppSidebarMenuSection,
};

export default AppSidebarMenu;
